import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ChevronDown, ChevronRight, BookOpen, Megaphone, Search, Pin, AlertTriangle, Info, AlertOctagon, Plus, Edit2, Trash2, Check, Award, Printer } from "lucide-react";
import { toast } from "sonner";
import { TRAINING_SECTIONS, getVisibleCategoriesForRole, getVisibleSectionsForRole } from "@/data/trainingContent";
import { confirm as brandedConfirm } from "@/components/ui/confirm-dialog";

/* Simple markdown-lite renderer: paragraphs, **bold**, - bullets, 1. numbered, > callouts, `code` */
function renderBody(body) {
  const lines = body.split("\n");
  const blocks = [];
  let list = null;
  let listType = null;
  let paragraph = [];

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ type: "p", text: paragraph.join(" ") });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list) {
      blocks.push({ type: listType, items: list });
      list = null;
      listType = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushParagraph();
      flushList();
      continue;
    }
    if (line.startsWith("> ")) {
      flushParagraph();
      flushList();
      blocks.push({ type: "callout", text: line.slice(2) });
      continue;
    }
    if (/^\s*[-*]\s/.test(line)) {
      flushParagraph();
      if (listType !== "ul") {
        flushList();
        list = [];
        listType = "ul";
      }
      list.push(line.replace(/^\s*[-*]\s+/, ""));
      continue;
    }
    if (/^\s*\d+\.\s/.test(line)) {
      flushParagraph();
      if (listType !== "ol") {
        flushList();
        list = [];
        listType = "ol";
      }
      list.push(line.replace(/^\s*\d+\.\s+/, ""));
      continue;
    }
    flushList();
    paragraph.push(line);
  }
  flushParagraph();
  flushList();

  const renderInline = (text) => {
    // **bold** and `code`
    const parts = [];
    const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
    let last = 0;
    let match;
    while ((match = re.exec(text)) !== null) {
      if (match.index > last) parts.push(text.slice(last, match.index));
      const m = match[0];
      const offset = match.index;
      if (m.startsWith("**")) {
        parts.push(<strong key={`b-${offset}`} className="font-semibold text-slate-900 dark:text-white">{m.slice(2, -2)}</strong>);
      } else {
        parts.push(<code key={`c-${offset}`} className="px-1.5 py-0.5 rounded bg-slate-800 text-purple-300 text-xs font-mono">{m.slice(1, -1)}</code>);
      }
      last = offset + m.length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
  };

  return blocks.map((b, i) => {
    const key = `${b.type}-${i}`;  // index in stable parser output; blocks never reorder
    if (b.type === "p") {
      return <p key={key} className="text-sm leading-relaxed text-slate-700 dark:text-slate-300 mb-3">{renderInline(b.text)}</p>;
    }
    if (b.type === "callout") {
      return (
        <div key={key} className="border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-900/20 pl-3 py-2 mb-3 rounded-r text-sm text-amber-900 dark:text-amber-200">
          {renderInline(b.text)}
        </div>
      );
    }
    if (b.type === "ul") {
      return (
        <ul key={key} className="list-disc pl-5 mb-3 space-y-1 text-sm text-slate-700 dark:text-slate-300">
          {b.items.map((item, j) => <li key={`${key}-li-${j}`}>{renderInline(item)}</li>)}
        </ul>
      );
    }
    if (b.type === "ol") {
      return (
        <ol key={key} className="list-decimal pl-5 mb-3 space-y-1 text-sm text-slate-700 dark:text-slate-300">
          {b.items.map((item, j) => <li key={`${key}-li-${j}`}>{renderInline(item)}</li>)}
        </ol>
      );
    }
    return null;
  });
}

const severityStyles = {
  info: { icon: Info, cls: "border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700 text-blue-900 dark:text-blue-200" },
  warning: { icon: AlertTriangle, cls: "border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 text-amber-900 dark:text-amber-200" },
  critical: { icon: AlertOctagon, cls: "border-rose-300 bg-rose-50 dark:bg-rose-900/20 dark:border-rose-700 text-rose-900 dark:text-rose-200" },
};

function AnnouncementEditor({ initial, onSave, onCancel }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [body, setBody] = useState(initial?.body || "");
  const [severity, setSeverity] = useState(initial?.severity || "info");
  const [audience, setAudience] = useState(initial?.audience || "all");
  const [pinned, setPinned] = useState(initial?.pinned || false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error("Title and body required");
      return;
    }
    setSaving(true);
    try {
      await onSave({ title, body, severity, audience, pinned });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="training-announcement-editor">
      <Input placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="ann-title-input" />
      <Textarea placeholder="Body (supports **bold**, `code`, - bullets, 1. numbered, > callouts)" rows={6} value={body} onChange={(e) => setBody(e.target.value)} data-testid="ann-body-input" />
      <div className="grid grid-cols-2 gap-3">
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger data-testid="ann-severity-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="info">Info</SelectItem>
            <SelectItem value="warning">Warning</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
          </SelectContent>
        </Select>
        <Select value={audience} onValueChange={setAudience}>
          <SelectTrigger data-testid="ann-audience-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All staff</SelectItem>
            <SelectItem value="prescriber">Prescribers only</SelectItem>
            <SelectItem value="admin">Admin/IT only</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
        <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} data-testid="ann-pinned-checkbox" />
        Pin to top
      </label>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} data-testid="ann-cancel-btn">Cancel</Button>
        <Button onClick={save} disabled={saving} data-testid="ann-save-btn">{saving ? "Saving..." : "Save"}</Button>
      </div>
    </div>
  );
}

export default function StaffTrainingManual() {
  const { user } = useAuth();
  const role = user?.role || "prescriber";
  const isAdmin = role === "admin";
  const [search, setSearch] = useState("");
  const [openSections, setOpenSections] = useState({});
  const [announcements, setAnnouncements] = useState([]);
  const [loadingAnn, setLoadingAnn] = useState(true);
  const [showAnnModal, setShowAnnModal] = useState(false);
  const [editingAnn, setEditingAnn] = useState(null);

  // ----- Training progress & certification -----
  const [readIds, setReadIds] = useState(new Set());
  const [latestCompletion, setLatestCompletion] = useState(null);
  const [generating, setGenerating] = useState(false);

  const categories = useMemo(() => getVisibleCategoriesForRole(role), [role]);
  const sections = useMemo(() => getVisibleSectionsForRole(role), [role]);
  const requiredSectionIds = useMemo(
    // "Recent Updates" / changelog entries are tagged required: false so
    // they appear in the manual for browsing but DON'T count against the
    // completion percentage (otherwise every new feature ship drops every
    // prescriber's progress and re-locks their certificate).
    () => sections.filter((s) => s.required !== false).map((s) => s.id),
    [sections]
  );
  const readCount = useMemo(() => {
    return requiredSectionIds.filter((id) => readIds.has(id)).length;
  }, [requiredSectionIds, readIds]);
  const totalRequired = requiredSectionIds.length;
  const progressPct = totalRequired === 0 ? 0 : Math.round((readCount / totalRequired) * 100);
  const isComplete = totalRequired > 0 && readCount >= totalRequired;

  const loadProgress = useCallback(async () => {
    try {
      const { data } = await API.get("/training/progress");
      setReadIds(new Set(data.read_section_ids || []));
    } catch {
      /* non-critical */
    }
  }, []);
  const loadLatest = useCallback(async () => {
    try {
      const { data } = await API.get("/training/completion/mine");
      setLatestCompletion(data.latest || null);
    } catch {
      /* non-critical */
    }
  }, []);
  useEffect(() => { loadProgress(); loadLatest(); }, [loadProgress, loadLatest]);

  /**
   * Deep-link support — when the URL hash is `#training:<sectionId>` (set
   * by the inline `<HelpLink />` buttons sprinkled across the dashboards),
   * auto-expand that section and scroll it into view. Listens to hash
   * changes so a link clicked while the manual is already open also works.
   */
  useEffect(() => {
    const focusFromHash = () => {
      const h = (window.location.hash || "").replace(/^#/, "");
      if (!h.startsWith("training:")) return;
      const targetId = h.slice("training:".length);
      if (!targetId) return;
      setSearch(""); // clear filter so the section is reachable
      setOpenSections((prev) => ({ ...prev, [targetId]: true }));
      // Wait for the accordion to render before scrolling.
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="section-${targetId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-purple-400", "ring-offset-2", "dark:ring-offset-slate-900");
          setTimeout(() => {
            el.classList.remove("ring-2", "ring-purple-400", "ring-offset-2", "dark:ring-offset-slate-900");
          }, 2400);
        }
      }, 100);
    };
    focusFromHash();
    window.addEventListener("hashchange", focusFromHash);
    return () => window.removeEventListener("hashchange", focusFromHash);
  }, []);

  const toggleRead = async (sectionId) => {
    const currentlyRead = readIds.has(sectionId);
    // Optimistic
    const next = new Set(readIds);
    if (currentlyRead) next.delete(sectionId); else next.add(sectionId);
    setReadIds(next);
    try {
      await API.put(`/training/progress/${sectionId}`, { read: !currentlyRead });
    } catch {
      toast.error("Failed to save progress");
      loadProgress(); // revert
    }
  };

  const downloadCert = async (completionId) => {
    try {
      const res = await API.get(
        `/training/completion/${completionId}/certificate.pdf`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "GLPFlo_Training_Certificate.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not fetch certificate");
    }
  };

  const generateCert = async () => {
    setGenerating(true);
    try {
      const { data } = await API.post("/training/completion", {
        section_ids: requiredSectionIds,
        role,
      });
      toast.success("Training completed — generating your certificate");
      setLatestCompletion(data);
      await downloadCert(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate certificate");
    } finally {
      setGenerating(false);
    }
  };

  const filteredSections = useMemo(() => {
    if (!search.trim()) return sections;
    const q = search.toLowerCase();
    return sections.filter(
      (s) => s.title.toLowerCase().includes(q) || s.body.toLowerCase().includes(q)
    );
  }, [search, sections]);

  const sectionsByCategory = useMemo(() => {
    const map = {};
    filteredSections.forEach((s) => {
      if (!map[s.category]) map[s.category] = [];
      map[s.category].push(s);
    });
    return map;
  }, [filteredSections]);

  const loadAnnouncements = async () => {
    try {
      const res = await API.get("/training/announcements");
      setAnnouncements(res.data.announcements || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingAnn(false);
    }
  };

  useEffect(() => { loadAnnouncements(); }, []);

  const saveAnnouncement = async (data) => {
    try {
      if (editingAnn) {
        await API.put(`/training/announcements/${editingAnn.id}`, data);
        toast.success("Announcement updated");
      } else {
        await API.post("/training/announcements", data);
        toast.success("Announcement posted");
      }
      setShowAnnModal(false);
      setEditingAnn(null);
      loadAnnouncements();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const deleteAnnouncement = async (ann) => {
    if (!(await brandedConfirm({ title: 'Delete?', description: `Delete announcement "${ann.title}"?`, confirmText: 'Delete', cancelText: 'Cancel', tone: 'danger' }))) return;
    try {
      await API.delete(`/training/announcements/${ann.id}`);
      toast.success("Announcement deleted");
      loadAnnouncements();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const toggle = (id) => setOpenSections((s) => ({ ...s, [id]: !s[id] }));

  const expandAll = () => {
    const all = {};
    filteredSections.forEach((s) => { all[s.id] = true; });
    setOpenSections(all);
  };
  const collapseAll = () => setOpenSections({});

  // Auto-expand when searching
  useEffect(() => {
    if (search.trim() && filteredSections.length < TRAINING_SECTIONS.length) {
      const all = {};
      filteredSections.forEach((s) => { all[s.id] = true; });
      setOpenSections(all);
    }
  }, [search, filteredSections]);

  return (
    <div className="space-y-6" data-testid="training-manual">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-600/20 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Staff Training Manual</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">Every function, every workflow — your go-to reference.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={expandAll} data-testid="expand-all-btn">Expand all</Button>
          <Button size="sm" variant="outline" onClick={collapseAll} data-testid="collapse-all-btn">Collapse all</Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          className="pl-9"
          placeholder="Search the manual (e.g. 'pre-screened', 'webhook', 'BAA')..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="training-search-input"
        />
      </div>

      {/* Training progress + certificate */}
      <div className="border rounded-xl bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-slate-900 dark:to-slate-800 dark:border-slate-800 p-4" data-testid="training-progress-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-purple-600/20 flex items-center justify-center">
              <Award className="w-4 h-4 text-purple-600 dark:text-purple-300" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900 dark:text-white">HIPAA Training Progress</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Mark each section &quot;I&apos;ve read this&quot; — generate your annual certificate at 100%.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {latestCompletion && (
              <Button size="sm" variant="outline" onClick={() => downloadCert(latestCompletion.id)} data-testid="training-reprint-cert-btn">
                <Printer className="w-4 h-4 mr-1" /> My certificate
              </Button>
            )}
            <Button
              size="sm"
              onClick={generateCert}
              disabled={!isComplete || generating}
              className="bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
              data-testid="training-generate-cert-btn"
            >
              <Award className="w-4 h-4 mr-1" />
              {generating ? "Generating…" : isComplete ? "Get certificate" : `${progressPct}% — finish to unlock`}
            </Button>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <div className="flex-1 h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
            <div
              className={`h-full transition-all ${isComplete ? "bg-emerald-500" : "bg-purple-500"}`}
              style={{ width: `${progressPct}%` }}
              data-testid="training-progress-bar"
            />
          </div>
          <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 tabular-nums">
            {readCount}/{totalRequired}
          </span>
        </div>
        {latestCompletion && (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
            Last certificate issued {new Date(latestCompletion.completed_at).toLocaleDateString()} · valid through {new Date(latestCompletion.expires_at).toLocaleDateString()}
          </p>
        )}
      </div>

      {/* Announcements (admin-editable) */}
      <div className="border rounded-xl bg-white dark:bg-slate-900 dark:border-slate-800" data-testid="training-announcements">
        <div className="flex items-center justify-between p-4 border-b dark:border-slate-800">
          <div className="flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            <h3 className="font-semibold text-slate-900 dark:text-white">Announcements & Updates</h3>
          </div>
          {isAdmin && (
            <Button size="sm" onClick={() => { setEditingAnn(null); setShowAnnModal(true); }} data-testid="new-announcement-btn">
              <Plus className="w-4 h-4 mr-1" /> New
            </Button>
          )}
        </div>
        <div className="p-4 space-y-2">
          {loadingAnn ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
          ) : announcements.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {isAdmin ? "No announcements yet — click New to post one for staff." : "No announcements at this time."}
            </p>
          ) : (
            announcements.map((a) => {
              const { icon: Icon, cls } = severityStyles[a.severity] || severityStyles.info;
              return (
                <div key={a.id} className={`border rounded-lg p-3 ${cls}`} data-testid={`announcement-${a.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                      <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold text-sm">{a.title}</h4>
                          {a.pinned && <Pin className="w-3 h-3" />}
                          <span className="text-xs opacity-70">{new Date(a.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="mt-1 text-sm">{renderBody(a.body)}</div>
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={() => { setEditingAnn(a); setShowAnnModal(true); }} className="p-1 hover:bg-black/10 rounded" data-testid={`ann-edit-${a.id}`}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => deleteAnnouncement(a)} className="p-1 hover:bg-black/10 rounded" data-testid={`ann-delete-${a.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Categories with accordion sections */}
      <div className="space-y-4">
        {categories.map((cat) => {
          const catSections = sectionsByCategory[cat.key] || [];
          if (catSections.length === 0) return null;
          return (
            <div key={cat.key} className="border rounded-xl bg-white dark:bg-slate-900 dark:border-slate-800 overflow-hidden" data-testid={`category-${cat.key}`}>
              <div className="px-4 py-3 border-b dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                <h3 className="font-semibold text-slate-900 dark:text-white text-sm">
                  {cat.label}
                  <span className="ml-2 text-xs font-normal text-slate-500 dark:text-slate-400">({catSections.length})</span>
                </h3>
              </div>
              <div className="divide-y dark:divide-slate-800">
                {catSections.map((s) => {
                  const open = !!openSections[s.id];
                  const isRead = readIds.has(s.id);
                  return (
                    <div key={s.id} data-testid={`section-${s.id}`}>
                      <button
                        onClick={() => toggle(s.id)}
                        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                        data-testid={`section-toggle-${s.id}`}
                      >
                        {open ? <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                        <span className="font-medium text-sm text-slate-800 dark:text-slate-200 flex-1">{s.title}</span>
                        {s.required === false && (
                          <span
                            className="text-[10px] uppercase font-bold tracking-wide text-purple-600 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30 rounded-full px-2 py-0.5"
                            title="Informational only — doesn't count toward your certificate completion"
                            data-testid={`section-optional-${s.id}`}
                          >
                            Info
                          </span>
                        )}
                        {isRead && (
                          <span className="text-[10px] uppercase font-bold tracking-wide text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 rounded-full px-2 py-0.5 flex items-center gap-1">
                            <Check className="w-3 h-3" /> Read
                          </span>
                        )}
                      </button>
                      {open && (
                        <div className="px-11 pb-4 pt-1">
                          {renderBody(s.body)}
                          <button
                            type="button"
                            onClick={() => toggleRead(s.id)}
                            className={`mt-3 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${isRead
                              ? "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-300"
                              : "bg-white border-purple-200 text-purple-700 hover:bg-purple-50 dark:bg-slate-800 dark:border-purple-700 dark:text-purple-300"}`}
                            data-testid={`section-read-toggle-${s.id}`}
                          >
                            <Check className="w-3.5 h-3.5" />
                            {isRead ? "Marked read — click to undo" : "I've read this"}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {filteredSections.length === 0 && (
          <div className="text-center py-10 text-sm text-slate-500 dark:text-slate-400">
            No sections match &quot;{search}&quot;. Try a different search term.
          </div>
        )}
      </div>

      {/* Announcement modal */}
      <Dialog open={showAnnModal} onOpenChange={(o) => { if (!o) { setShowAnnModal(false); setEditingAnn(null); } }}>
        <DialogContent className="max-w-lg" data-testid="announcement-modal">
          <DialogHeader>
            <DialogTitle>{editingAnn ? "Edit Announcement" : "New Announcement"}</DialogTitle>
          </DialogHeader>
          <AnnouncementEditor
            initial={editingAnn}
            onSave={saveAnnouncement}
            onCancel={() => { setShowAnnModal(false); setEditingAnn(null); }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
